import { useEffect, useState } from 'react';
import { Field, Skeleton, useAsync, useToast } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useSession } from '../../lib/session';
import { pilotError } from '../../pilot/context';
import { PilotSettings, PilotStatus } from '../../pilot/model';

const list = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);

/**
 * «Настройки Пилота» (SPEC §2.5, §7): sender, shop link and allowed hosts, issued promo codes, default send time,
 * approval TTL and the typed-echo threshold, AI on/off and its budget. Changing the sender, hosts or codes voids open
 * approvals — they were given for other values.
 */
export function PilotSettingsCard() {
  const { me } = useSession();
  const toast = useToast();
  const s = useAsync(() => api.get<PilotSettings>('/pilot/settings'), []);
  const status = useAsync(() => api.get<PilotStatus>('/pilot/status'), []);
  const owner = me?.user.role === 'owner' || me?.user.role === 'admin';
  const [f, setF] = useState<Record<string, string | boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const d = s.data;
    if (!d) return;
    setF({
      default_from: d.default_from ?? '',
      default_from_name: d.default_from_name ?? '',
      currency: d.currency,
      shop_url: d.shop_url ?? '',
      allowed_link_hosts: d.allowed_link_hosts.join(', '),
      allowed_codes: d.allowed_codes.join(', '),
      default_send_local: d.default_send_local,
      approval_ttl_hours: String(d.approval_ttl_hours),
      strong_echo_recipients: String(d.strong_echo_recipients),
      ai_enabled: d.ai_enabled,
      ai_monthly_budget_usd: d.ai_monthly_budget_usd == null ? '' : String(d.ai_monthly_budget_usd),
    });
  }, [s.data]);
  // /settings#pilot (the link from the Pilot page) opens this card.
  useEffect(() => {
    if (s.data && window.location.hash === '#pilot') document.getElementById('pilot')?.scrollIntoView({ block: 'start' });
  }, [s.data]);

  if (s.error instanceof ApiError && s.error.code === 'PILOT_DISABLED') return null;
  const set = (k: string, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const str = (k: string) => String(f[k] ?? '');

  async function save() {
    setBusy(true);
    setError('');
    try {
      const r = await api.put<PilotSettings & { voided_approvals: number }>('/pilot/settings', {
        default_from: str('default_from').trim() || null,
        default_from_name: str('default_from_name').trim() || null,
        currency: str('currency').trim().toUpperCase(),
        shop_url: str('shop_url').trim() || null,
        allowed_link_hosts: list(str('allowed_link_hosts')).map((h) => h.toLowerCase()),
        allowed_codes: list(str('allowed_codes')),
        default_send_local: str('default_send_local'),
        approval_ttl_hours: Number(str('approval_ttl_hours')),
        strong_echo_recipients: Number(str('strong_echo_recipients')),
        ai_enabled: Boolean(f.ai_enabled),
        ai_monthly_budget_usd: str('ai_monthly_budget_usd').trim() ? Number(str('ai_monthly_budget_usd')) : null,
      });
      s.setData(r);
      toast(r.voided_approvals ? t('Сохранено. Открытых согласований аннулировано: {n}', { n: r.voided_approvals }) : t('Настройки Пилота сохранены'));
    } catch (e) {
      setError(pilotError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad stack" id="pilot">
      <h3>{t('Пилот')}</h3>
      {!s.data ? (
        <Skeleton h={160} />
      ) : (
        <>
          <div className="small muted">{t('Пилот берёт отсюда отправителя, ссылку магазина и промокоды — других он не подставит. Изменение отправителя, хостов или кодов аннулирует открытые согласования.')}</div>
          <div className="pilot-form-row">
            <Field label={t('Отправитель (email)')} hint={s.data.effective_from ? t('сейчас: {email}', { email: s.data.effective_from.email }) : t('нужен подтверждённый домен')}><input className="input" value={str('default_from')} disabled={!owner} onChange={(e) => set('default_from', e.target.value)} /></Field>
            <Field label={t('Имя отправителя')}><input className="input" value={str('default_from_name')} disabled={!owner} onChange={(e) => set('default_from_name', e.target.value)} /></Field>
          </div>
          <Field label={t('Ссылка магазина (кнопка письма)')}><input className="input" value={str('shop_url')} disabled={!owner} placeholder="https://shop.example.com" onChange={(e) => set('shop_url', e.target.value)} /></Field>
          <Field label={t('Разрешённые хосты ссылок')} hint={t('через запятую; другие ссылки в письме Пилот не поставит')}><input className="input input-mono" value={str('allowed_link_hosts')} disabled={!owner} onChange={(e) => set('allowed_link_hosts', e.target.value)} /></Field>
          <Field label={t('Выданные промокоды')} hint={t('через запятую; код не из списка Пилот не предложит')}><input className="input input-mono" value={str('allowed_codes')} disabled={!owner} onChange={(e) => set('allowed_codes', e.target.value)} /></Field>
          <div className="pilot-form-row">
            <Field label={t('Валюта')}><input className="input" maxLength={3} value={str('currency')} disabled={!owner} onChange={(e) => set('currency', e.target.value.toUpperCase())} /></Field>
            <Field label={t('Время «на сегодня» ({tz})', { tz: s.data.timezone })}><input className="input" type="time" value={str('default_send_local')} disabled={!owner} onChange={(e) => set('default_send_local', e.target.value)} /></Field>
          </div>
          <div className="pilot-form-row">
            <Field label={t('Согласование действует, ч')}><input className="input" inputMode="numeric" value={str('approval_ttl_hours')} disabled={!owner} onChange={(e) => set('approval_ttl_hours', e.target.value)} /></Field>
            <Field label={t('Вводить число получателей, если больше')} hint={t('для больших рассылок согласующий набирает число вручную')}><input className="input" inputMode="numeric" value={str('strong_echo_recipients')} disabled={!owner} onChange={(e) => set('strong_echo_recipients', e.target.value)} /></Field>
          </div>
          <div className="pilot-form-row">
            <label className="row small" style={{ cursor: owner ? 'pointer' : 'default' }}>
              <input type="checkbox" checked={Boolean(f.ai_enabled)} disabled={!owner} onChange={(e) => set('ai_enabled', e.target.checked)} /> {t('AI-планировщик (если подключён на установке)')}
            </label>
            <Field label={t('Бюджет AI в месяц, $')} hint={t('пусто — по умолчанию установки')}><input className="input" inputMode="decimal" value={str('ai_monthly_budget_usd')} disabled={!owner} onChange={(e) => set('ai_monthly_budget_usd', e.target.value)} /></Field>
          </div>
          {status.data && (
            <div className="small muted">
              {t('Сейчас: {mode} · AI в этом месяце ${used} из ${limit} · команд сегодня {today} из {todayLimit}', {
                mode: status.data.planner.mode === 'llm' ? `AI (${status.data.planner.model})` : t('правила без AI — все функции доступны'),
                used: status.data.ai_budget.month_used_usd.toFixed(2),
                limit: status.data.ai_budget.month_limit_usd.toFixed(2),
                today: status.data.ai_budget.today_commands,
                todayLimit: status.data.ai_budget.today_limit,
              })}
            </div>
          )}
          <div className="callout"><span><b>{t('Голос.')}</b> {t('Кнопка микрофона использует распознавание речи браузера: Chrome отправляет звук на серверы Google. MailOcean звук не получает и не хранит — только текст, который вы проверили и отправили.')}</span></div>
          {error && <div className="callout bad">{error}</div>}
          {owner ? <div><button type="button" className="btn btn-primary" disabled={busy} onClick={save}>{t('Сохранить настройки Пилота')}</button></div> : <div className="small muted">{t('Менять настройки Пилота могут владелец и администраторы.')}</div>}
        </>
      )}
    </div>
  );
}
